import * as amplitude from "@amplitude/analytics-browser";
import {sessionReplayPlugin} from "@amplitude/plugin-session-replay-browser";
import {UserContext} from "@hosttools/frontend";
import Intercom from "@intercom/messenger-js-sdk";
import Clarity from "@microsoft/clarity";
import LogRocket from "logrocket";
import type React from "react";
import {memo, useContext, useEffect} from "react";
import {FullStoryAPI} from "react-fullstory";
import TagManager from "react-gtm-module";
import {hotjar} from "react-hotjar";

import {useFacebookPixel} from "@/client/hooks/useFacebookPixel";

const ExternalServices: React.FC = () => {
    const {user, isAuthenticated, isLoading, visibleListings} = useContext(UserContext);

    // Initialize Facebook Pixel
    useFacebookPixel();

    useEffect(() => {
        if (__DEV__) {
            return;
        }

        if (isLoading) {
            return;
        }

        // Initialize other tracking services
        TagManager.initialize({
            gtmId: "GTM-59XQPWR",
            dataLayer: user?._id
                ? {
                      userId: user._id
                  }
                : undefined
        });

        // Get existing device ID to maintain session continuity
        const existingDeviceId = amplitude.getDeviceId();

        amplitude.init(
            __IS_BETA__ ? "cefdbd6489077ed0d28775e98db636c4" : "a492997e80b319166504a80bdb91e7f9",
            user?._id ? user._id : undefined,
            {
                deviceId: existingDeviceId, // Preserve device ID for session continuity
                autocapture: {
                    attribution: true,
                    pageViews: true,
                    sessions: true,
                    formInteractions: false,
                    fileDownloads: false,
                    elementInteractions: false
                }
            }
        );
        Clarity.init("scvahv52ct");

        if (!__IS_BETA__) {
            LogRocket.init("acu09k/superhost-tools");
            hotjar.initialize({id: 1683480, sv: 6});
        }

        if (user?._id) {
            // Set user ID to connect anonymous events to identified user
            amplitude.setUserId(user._id);

            // Identify user with properties for better attribution

            const identifyObj = new amplitude.Identify().set("email", user.username);

            // Only set properties if they exist (avoid empty strings)
            if (user.firstName) identifyObj.set("firstName", user.firstName);
            if (user.lastName) identifyObj.set("lastName", user.lastName);
            if (user.subscriptionStatus)
                identifyObj.set("subscriptionStatus", user.subscriptionStatus);

            amplitude.identify(identifyObj);

            Clarity.identify(user._id);

            if (!__IS_BETA__) {
                LogRocket.identify(user._id, {
                    email: user.username,
                    name: user.fullName
                    // // Add your own custom user variables here, ie:
                    // subscriptionType: "pro"
                });
                hotjar.identify(user._id, {
                    email: user.username,
                    name: user.fullName
                });

                FullStoryAPI("identify", user._id, {
                    displayName: user.fullName,
                    email: user.username
                });
            }
        }

        const sessionReplayTracking = sessionReplayPlugin({
            sampleRate: 1 // record all sessions
        });
        amplitude.add(sessionReplayTracking);
    }, [
        isLoading,
        user._id,
        user.firstName,
        user.lastName,
        user.fullName,
        user.username,
        user.subscriptionStatus,
        visibleListings.length
    ]);

    // Intercom
    useEffect(() => {
        if (INTERCOM_APP_ID) {
            if (!isAuthenticated) {
                Intercom({
                    app_id: INTERCOM_APP_ID
                });
                return;
            }

            Intercom({
                app_id: INTERCOM_APP_ID,
                user_id: user._id,
                name: user.fullName,
                email: user.username,
                user_hash: user.intercomHash,
                created_at: user.createdAt ? new Date(user.createdAt).getTime() / 1000 : undefined,
                server: user.isBeta ? "beta" : "main",
                status: user.isBeta ? "beta" : user.subscriptionStatus,
                listings: visibleListings.length
            });
        }
    }, [
        isAuthenticated,
        user._id,
        user.fullName,
        user.username,
        user.intercomHash,
        user.createdAt,
        user.isBeta,
        user.subscriptionStatus,
        visibleListings.length
    ]);

    return null;
};

export default memo(ExternalServices);
